// Enhanced neuromorphic network with proper dendritic branching
declare const THREE: any

interface Vector3 {
  x: number
  y: number
  z: number
  clone(): Vector3
  normalize(): Vector3
  add(v: Vector3): Vector3
  sub(v: Vector3): Vector3 // Add subtract method
  multiplyScalar(s: number): Vector3
  distanceTo(v: Vector3): number
  cross(v: Vector3): Vector3
  lerp(v: Vector3, t: number): Vector3
  copy(v: Vector3): Vector3
  setLength(length: number): Vector3
  applyAxisAngle(axis: Vector3, angle: number): Vector3
  dot(v: Vector3): number
  set(x: number, y: number, z: number): Vector3
  length(): number // Add length method
}

interface CatmullRomCurve3 {
  tension: number
  getLength(): number
  getPointAt(t: number): Vector3
}

// Enhanced neuron structure with realistic properties
export interface Neuron {
  id: string
  position: Vector3
  activation: number
  excitability: number // How easily the neuron activates (0-1)
  refractoryPeriod: number // Time until neuron can fire again
  lastFired: number
  glowIntensity: number
  lastActivation: number
  lastActiveTime?: number // Timestamp of last active event (optional)
  connections: Connection[]
  dendrites: DendriticTree
  axon: AxonStructure
  neuronType: "excitatory" | "inhibitory"
  health: number // Neuron health (0-1, affects survival)
  age: number // Time since creation
  layer: number // Network layer (for organization)
  // Added for neuron death logic
}

// Proper connection with realistic synaptic properties
export interface Connection {
  targetNeuronId: string
  weight: number // Synaptic strength (0-1)
  delay: number // Propagation delay in ms
  lastUsed: number
  usageCount: number
  synapseType: "excitatory" | "inhibitory"
  plasticity: number // How much the connection can change
  activationIntensity?: number
}

// Tree-like dendritic structure
export interface DendriticTree {
  branches: DendriticBranch[]
  totalLength: number
  complexity: number
}

export interface DendriticBranch {
  id: string
  parentBranch: string | null
  children: string[]
  points: Vector3[]
  curve: CatmullRomCurve3
  diameter: number
  length: number
  activity: number
  strength: number
  lastActive: number
  branchOrder: number // 0 = primary, 1 = secondary, etc.
  synapses: Synapse[]
  growthDirection: Vector3
  isGrowing: boolean
}

export interface Synapse {
  id: string
  position: Vector3
  strength: number
  fromNeuronId: string
  active: boolean
  lastActivation: number
  plasticity: number
  type: "excitatory" | "inhibitory"
}

export interface AxonStructure {
  mainBranch: Vector3[]
  terminals: AxonTerminal[]
  myelinated: boolean
  conductionSpeed: number
}

export interface AxonTerminal {
  position: Vector3
  targetNeuronId: string
  synapses: Synapse[]
}

export interface ActivePulse {
  id: string
  sourceNeuronId: string
  targetNeuronId: string
  branchId: string
  progress: number
  intensity: number
  position: Vector3
  speed: number
  type: "excitatory" | "inhibitory"
  startTime: number
}

export interface NetworkParams {
  // Basic network structure
  neuronCount: number
  networkSize: { x: number; y: number; z: number }
  neuronBodySize: number
  neuronGlowSize: number
  neuronColor: number
  glowColor: number

  // Dendritic parameters
  maxDendriticBranches: number
  maxBranchOrder: number
  branchingProbability: number
  branchLengthRange: { min: number; max: number }
  branchAngleVariation: number
  dendriteRadius: number // (keep only one occurrence)
  dendriticFieldRadius: number

  // Connection parameters
  connectionRadius: number
  maxConnectionsPerNeuron: number
  synapticStrengthRange: { min: number; max: number }

  // Activity parameters
  activationThreshold: number
  refractoryPeriod: number
  conductionSpeed: number
  synapticDelay: number

  // Plasticity parameters
  learningRate: number
  strengthDecayRate: number
  pruningThreshold: number
  growthThreshold: number

  // Visual parameters
  dendriteColor: number
  activationSpeed: number
  propagationChance: number
  decayRate: number
  pulseIntensity: number
  spontaneousActivationRate: number
  bloomStrength: number
  bloomRadius: number
  bloomThreshold: number
  branchGrowthRate: number
  branchPruningThreshold: number
  branchPruningRate: number
  maxNetworkBranches: number
  growthStrengthThreshold: number
  neuronInactivityThreshold: number
  neuronHealthDecayRate: number
  minNeuronHealthForSurvival: number
  enableDendrites: boolean // New parameter to control dendrite creation
}

import { debugLog } from "../../utils/debug"

export class DendriticNetwork {
  private THREE: any
  public neurons: Neuron[] = []
  public activePulses: ActivePulse[] = []
  public params: NetworkParams
  private nextNeuronId = 0
  private nextBranchId = 0
  private nextSynapseId = 0
  private nextPulseId = 0

  // --- Integrate Octree for neuron spatial indexing ---
  private octree: Octree | null = null

  constructor(THREE: any, params: NetworkParams) {
    this.THREE = THREE
    this.params = params
  }

  private generateId(prefix: string): string {
    switch (prefix) {
      case "neuron":
        return `neuron_${this.nextNeuronId++}`
      case "branch":
        return `branch_${this.nextBranchId++}`
      case "synapse":
        return `synapse_${this.nextSynapseId++}`
      case "pulse":
        return `pulse_${this.nextPulseId++}`
      default:
        return `${prefix}_${Math.random()}`
    }
  }

  private randomVec3(scale = 1): Vector3 {
    return new this.THREE.Vector3(
      (Math.random() - 0.5) * scale,
      (Math.random() - 0.5) * scale,
      (Math.random() - 0.5) * scale,
    )
  }

  private randomDirection(): Vector3 {
    return this.randomVec3(1).normalize()
  }

  // Create simple neuron placement
  public createNeurons(): void {
    this.neurons = [];
    debugLog(`Creating ${this.params.neuronCount} neurons...`);

    // Create neurons in 3D space with some clustering
    for (let i = 0; i < this.params.neuronCount; i++) {
      // Create clusters for more interesting connectivity
      const clusterIndex = Math.floor(i / 20); // 20 neurons per cluster
      const clusterCenter = new this.THREE.Vector3(
        (Math.random() - 0.5) * this.params.networkSize.x * 0.8,
        (Math.random() - 0.5) * this.params.networkSize.y * 0.8,
        (Math.random() - 0.5) * this.params.networkSize.z * 0.8,
      );

      // Position within cluster with some spread
      const position = clusterCenter
        .clone()
        .add(
          new this.THREE.Vector3(
            (Math.random() - 0.5) * this.params.networkSize.x * 0.2,
            (Math.random() - 0.5) * this.params.networkSize.y * 0.2,
            (Math.random() - 0.5) * this.params.networkSize.z * 0.2,
          ),
        );

      const neuron: Neuron = {
        id: this.generateId("neuron"),
        position,
        activation: 0,
        excitability: 0.4 + Math.random() * 0.3,
        refractoryPeriod: this.params.refractoryPeriod || 50,
        lastFired: -1000,
        glowIntensity: 1,
        lastActivation: 0,
        connections: [],
        dendrites: {
          branches: [],
          totalLength: 0,
          complexity: 0,
        },
        axon: {
          mainBranch: [],
          terminals: [],
          myelinated: true,
          conductionSpeed: this.params.conductionSpeed || 80,
        },
        neuronType: Math.random() > 0.15 ? "excitatory" : "inhibitory", // 85% excitatory
        health: 1.0,
        age: 0,
        layer: clusterIndex,
        lastActiveTime: Date.now(),
        // ...existing code...
      };

      this.neurons.push(neuron);
    }

    debugLog(`Created ${this.neurons.length} neurons`);

    // --- Build Octree for neuron spatial indexing ---
    const min = new this.THREE.Vector3(
      -this.params.networkSize.x / 2,
      -this.params.networkSize.y / 2,
      -this.params.networkSize.z / 2,
    );
    const max = new this.THREE.Vector3(
      this.params.networkSize.x / 2,
      this.params.networkSize.y / 2,
      this.params.networkSize.z / 2,
    );
    this.octree = new Octree({ min, max });
    for (const neuron of this.neurons) {
      this.octree.insert(neuron, neuron.position);
    }
  }

  // Create simple dendrites and axon connections
  public createDendriticBranches(): void {
    debugLog("Creating dendrites and connections...")

    // Create simple dendrites for each neuron
    for (let neuron of this.neurons) {
      this.createSimpleDendrites(neuron)
    }

    // Create simple axon connections between neurons
    this.createSimpleConnections()
  }

  private growDendriticTree(neuron: Neuron): void {
    const dendrites = neuron.dendrites
    dendrites.branches = []

    // Create primary dendrites (major branches from cell body)
    const primaryCount = 3 + Math.floor(Math.random() * 4) // 3-6 primary dendrites

    for (let i = 0; i < primaryCount; i++) {
      const direction = this.randomDirection()
      // Bias dendrites away from cell body and upward (toward inputs)
      direction.y += 0.3
      direction.normalize()

      const primaryBranch = this.createDendriticBranch(
        neuron,
        null,
        direction,
        0, // branch order
        this.params.branchLengthRange.min +
          Math.random() *
            (this.params.branchLengthRange.max -
              this.params.branchLengthRange.min),
      )

      dendrites.branches.push(primaryBranch)

      // Recursively create secondary and tertiary branches
      this.growChildBranches(neuron, primaryBranch, 1)
    }

    // Calculate dendritic tree complexity
    dendrites.totalLength = dendrites.branches.reduce(
      (sum, branch) => sum + branch.length,
      0,
    )
    dendrites.complexity = dendrites.branches.length
  }

  private createDendriticBranch(
    neuron: Neuron,
    parentBranch: DendriticBranch | null,
    direction: Vector3,
    branchOrder: number,
    targetLength: number,
  ): DendriticBranch {
    const startPos = parentBranch
      ? parentBranch.points[parentBranch.points.length - 1].clone()
      : neuron.position.clone()

    const points: Vector3[] = [startPos]
    let currentPos = startPos.clone()
    let currentDir = direction.clone()

    // Create segments with realistic dendritic branching patterns
    const segments = Math.max(3, Math.floor(targetLength / 3)) // More segments for smoother curves
    const segmentLength = targetLength / segments

    for (let i = 1; i <= segments; i++) {
      // Add curvature and slight randomness (dendrites aren't straight)
      const randomOffset = this.randomVec3(0.3)
      randomOffset.multiplyScalar(Math.sin((i / segments) * Math.PI)) // More curve in middle

      currentDir.add(randomOffset).normalize()

      // Gradually taper the segment length
      const taperFactor = 1 - (i / segments) * 0.3
      const actualSegmentLength = segmentLength * taperFactor

      currentPos = currentPos
        .clone()
        .add(currentDir.clone().multiplyScalar(actualSegmentLength))
      points.push(currentPos)
    }

    // Create smooth curve through points
    const curve = new this.THREE.CatmullRomCurve3(points)
    curve.tension = 0.4 // Natural curve tension

    const branch: DendriticBranch = {
      id: this.generateId("branch"),
      parentBranch: parentBranch?.id || null,
      children: [],
      points,
      curve,
      diameter: Math.max(0.1, 0.5 - branchOrder * 0.1), // Taper with branch order
      length: curve.getLength(),
      activity: 0,
      strength: 0.5 + Math.random() * 0.3,
      lastActive: 0,
      branchOrder,
      synapses: [],
      growthDirection: direction.clone(),
      isGrowing:
        branchOrder < (this.params.maxBranchOrder || 4) && Math.random() > 0.5,
    }

    // Add parent-child relationship
    if (parentBranch) {
      parentBranch.children.push(branch.id)
    }

    return branch
  }

  private growChildBranches(
    neuron: Neuron,
    parentBranch: DendriticBranch,
    branchOrder: number,
  ): void {
    if (branchOrder >= (this.params.maxBranchOrder || 4)) return

    // Probability of branching decreases with branch order
    const branchingProb =
      (this.params.branchingProbability || 0.6) * Math.pow(0.6, branchOrder)

    if (Math.random() > branchingProb) return

    // Create 1-3 child branches
    const childCount = Math.random() > 0.7 ? (Math.random() > 0.9 ? 3 : 2) : 1

    for (let i = 0; i < childCount; i++) {
      // Branch at angles from parent direction
      const baseDirection = parentBranch.growthDirection.clone()
      const branchAngle =
        (Math.random() - 0.5) *
        (this.params.branchAngleVariation || Math.PI / 3)

      // Create perpendicular vector for rotation
      const perpendicular = new this.THREE.Vector3(1, 0, 0)
      if (Math.abs(baseDirection.dot(perpendicular)) > 0.9) {
        perpendicular.set(0, 1, 0)
      }
      perpendicular.cross(baseDirection).normalize()

      const direction = baseDirection
        .clone()
        .applyAxisAngle(perpendicular, branchAngle)

      // Shorter branches at higher orders
      const lengthScale = Math.pow(0.7, branchOrder)
      const targetLength =
        (this.params.branchLengthRange.min +
          Math.random() *
            (this.params.branchLengthRange.max -
              this.params.branchLengthRange.min)) *
        lengthScale

      const childBranch = this.createDendriticBranch(
        neuron,
        parentBranch,
        direction,
        branchOrder,
        targetLength,
      )
      neuron.dendrites.branches.push(childBranch)

      // Recursively create more branches
      this.growChildBranches(neuron, childBranch, branchOrder + 1)
    }
  }

  private createNeuralConnections(): void {
    debugLog("Creating neural connections...")

    for (let i = 0; i < this.neurons.length; i++) {
      const sourceNeuron = this.neurons[i]
      const connections: Connection[] = []
      const connectedNeuronIds = new Set<string>() // Prevent duplicate connections

      // Find nearby neurons within connection radius
      const nearbyNeurons = this.findNearbyNeurons(sourceNeuron, i)

      // Create connections with higher probability for closer neurons
      for (let target of nearbyNeurons) {
        if (connections.length >= (this.params.maxConnectionsPerNeuron || 6))
          break

        const targetNeuron = this.neurons[target.index]

        // Skip if already connected to this neuron
        if (connectedNeuronIds.has(targetNeuron.id)) continue

        // Higher connection probability for closer neurons
        const connectionProb = Math.max(
          0.3,
          0.8 - target.distance / this.params.connectionRadius,
        )

        if (Math.random() < connectionProb) {
          const connection: Connection = {
            targetNeuronId: targetNeuron.id,
            weight:
              this.params.synapticStrengthRange.min +
              Math.random() *
                (this.params.synapticStrengthRange.max -
                  this.params.synapticStrengthRange.min),
            delay: Math.max(
              5,
              (this.params.synapticDelay || 10) +
                target.distance / (this.params.conductionSpeed || 50),
            ),
            lastUsed: 0,
            usageCount: 0,
            synapseType: sourceNeuron.neuronType,
            plasticity: 0.1 + Math.random() * 0.2,
          }

          connections.push(connection)
          connectedNeuronIds.add(targetNeuron.id)

          // Create synapses on dendritic branches
          this.createSynapse(sourceNeuron, targetNeuron, connection)
        }
      }

      sourceNeuron.connections = connections
    }

    const totalConnections = this.neurons.reduce(
      (sum, n) => sum + n.connections.length,
      0,
    )
    debugLog(`Created ${totalConnections} neural connections (no duplicates)`)
    debugLog(
      `Average connections per neuron: ${(totalConnections / this.neurons.length).toFixed(1)}`,
    )
  }

  private findNearbyNeurons(
    neuron: Neuron,
    excludeIndex: number,
  ): Array<{ index: number; distance: number }> {
    const nearby: Array<{ index: number; distance: number }> = []

    for (let i = 0; i < this.neurons.length; i++) {
      if (i === excludeIndex) continue

      const distance = neuron.position.distanceTo(this.neurons[i].position)
      if (distance <= (this.params.connectionRadius || 60)) {
        nearby.push({ index: i, distance })
      }
    }

    // Sort by distance and return closest
    return nearby.sort((a, b) => a.distance - b.distance)
  }

  private checkDendriticOverlap(
    sourceNeuron: Neuron,
    targetNeuron: Neuron,
  ): boolean {
    // Simple overlap check - in reality this would be more sophisticated
    const distance = sourceNeuron.position.distanceTo(targetNeuron.position)
    const overlapRadius = this.params.dendriticFieldRadius || 40

    return distance <= overlapRadius
  }

  private createSynapse(
    sourceNeuron: Neuron,
    targetNeuron: Neuron,
    connection: Connection,
  ): void {
    // Find a suitable dendritic branch on target neuron
    const targetBranches = targetNeuron.dendrites.branches
    if (targetBranches.length === 0) return

    // Prefer branches closer to the source neuron
    let bestBranch = targetBranches[0]
    let minDistance = Infinity

    for (let branch of targetBranches) {
      const branchMidpoint = branch.points[Math.floor(branch.points.length / 2)]
      const distance = sourceNeuron.position.distanceTo(branchMidpoint)
      if (distance < minDistance) {
        minDistance = distance
        bestBranch = branch
      }
    }

    // Create synapse at random point along the branch
    const synapsePosition = bestBranch.curve.getPointAt(Math.random())

    const synapse: Synapse = {
      id: this.generateId("synapse"),
      position: synapsePosition,
      strength: connection.weight,
      fromNeuronId: sourceNeuron.id,
      active: false,
      lastActivation: 0,
      plasticity: connection.plasticity,
      type: connection.synapseType,
    }

    bestBranch.synapses.push(synapse)
  }

  // Create activation pulse between connected neurons
  public createPulse(
    sourceNeuronId: string,
    targetNeuronId: string,
    intensity: number,
  ): void {
    const pulse: ActivePulse = {
      id: this.generateId("pulse"),
      sourceNeuronId,
      targetNeuronId,
      branchId: "", // Will be set when pulse reaches target dendrites
      progress: 0,
      intensity,
      position:
        this.getNeuronById(sourceNeuronId)?.position.clone() ||
        new this.THREE.Vector3(),
      speed: this.params.activationSpeed,
      type: this.getNeuronById(sourceNeuronId)?.neuronType || "excitatory",
      startTime: Date.now(),
    }

    this.activePulses.push(pulse)
  }

  public getNeuronById(id: string): Neuron | undefined {
    return this.neurons.find((n) => n.id === id)
  }

  public updateNetworkTopology(elapsedTime: number): void {
    // Remove completed pulses
    this.activePulses = this.activePulses.filter((pulse) => pulse.progress < 1)

    // Update neuron ages and health
    for (let neuron of this.neurons) {
      neuron.age += 0.01

      // Gradual health decay if unused
      if (elapsedTime - neuron.lastActivation > 10) {
        neuron.health *= 0.999
      }
    }

    // Prune weak connections and grow new ones based on activity
    this.adaptNetworkStructure(elapsedTime)
  }

  private adaptNetworkStructure(elapsedTime: number): void {
    // More aggressive adaptation for dynamic network behavior
    for (let neuron of this.neurons) {
      // Prune weak connections more aggressively
      neuron.connections = neuron.connections.filter((conn) => {
        const timeSinceUse = elapsedTime - conn.lastUsed

        // Prune weak connections that haven't been used recently
        if (
          timeSinceUse > 15 &&
          conn.weight < (this.params.pruningThreshold || 0.1)
        ) {
          debugLog(
            `Pruning weak connection from ${neuron.id} to ${conn.targetNeuronId}`,
          )
          return false // Remove weak, unused connections
        }

        // Decay unused connections
        if (timeSinceUse > 5) {
          conn.weight *= 1 - this.params.strengthDecayRate
        }

        return true
      })

      // Grow new dendrites if neuron is active
      if (
        neuron.activation > (this.params.growthThreshold || 0.7) &&
        Math.random() < this.params.branchGrowthRate
      ) {
        if (
          neuron.dendrites.branches.length <
          (this.params.maxNetworkBranches || 15)
        ) {
          this.growNewDendriticBranch(neuron)
        }
      }

      // Form new connections with nearby active neurons
      if (
        neuron.activation > 0.5 &&
        neuron.connections.length < this.params.maxConnectionsPerNeuron
      ) {
        this.tryFormNewConnection(neuron)
      }
    }

    // Remove neurons that have been inactive for too long (death)
    this.removeInactiveNeurons(elapsedTime)
  }

  private tryFormNewConnection(neuron: Neuron): void {
    // Find nearby neurons that don't already have connections
    const nearbyNeurons = this.neurons.filter(
      (n) =>
        n.id !== neuron.id &&
        n.position.distanceTo(neuron.position) <=
          this.params.connectionRadius &&
        !neuron.connections.some((conn) => conn.targetNeuronId === n.id) &&
        n.activation > 0.3, // Only connect to active neurons
    )

    if (nearbyNeurons.length > 0) {
      const targetNeuron =
        nearbyNeurons[Math.floor(Math.random() * nearbyNeurons.length)]

      // Create new connection
      const connection: Connection = {
        targetNeuronId: targetNeuron.id,
        weight:
          this.params.synapticStrengthRange.min +
          Math.random() *
            (this.params.synapticStrengthRange.max -
              this.params.synapticStrengthRange.min),
        delay: this.params.synapticDelay || 8,
        lastUsed: 0,
        usageCount: 0,
        synapseType: neuron.neuronType,
        plasticity: 0.1,
      }

      neuron.connections.push(connection)

      // Create axon terminal
      const terminal: AxonTerminal = {
        position: targetNeuron.position.clone(),
        targetNeuronId: targetNeuron.id,
        synapses: [],
      }

      neuron.axon.terminals.push(terminal)

      debugLog(`New connection formed from ${neuron.id} to ${targetNeuron.id}`)
    }
  }

  private removeInactiveNeurons(elapsedTime: number): void {
    // This method is replaced by pruneDeadNeurons
  }

  private pruneDeadNeurons(): void {
    const neuronsToRemove: string[] = []
    for (const neuron of this.neurons) {
      if (neuron.health <= this.params.minNeuronHealthForSurvival) {
        neuronsToRemove.push(neuron.id)
      }
    }

    for (const neuronId of neuronsToRemove) {
      this.removeNeuron(neuronId)
    }
  }

  public removeNeuron(neuronId: string): boolean {
    const neuronIndex = this.neurons.findIndex((n) => n.id === neuronId)
    if (neuronIndex === -1) return false

    const neuron = this.neurons[neuronIndex]

    // Remove all connections to this neuron from other neurons
    for (let otherNeuron of this.neurons) {
      otherNeuron.connections = otherNeuron.connections.filter(
        (conn) => conn.targetNeuronId !== neuronId,
      )

      // Remove axon terminals pointing to this neuron
      otherNeuron.axon.terminals = otherNeuron.axon.terminals.filter(
        (terminal) => terminal.targetNeuronId !== neuronId,
      )
    }

    // Remove the neuron
    this.neurons.splice(neuronIndex, 1)

    debugLog(`Removed dead neuron ${neuronId}`)
    return true
  }

  private growNewDendriticBranch(neuron: Neuron): void {
    // Find a suitable parent branch to extend
    const activeBranches = neuron.dendrites.branches.filter(
      (b) => b.activity > 0.3,
    )
    if (activeBranches.length === 0) return

    const parentBranch =
      activeBranches[Math.floor(Math.random() * activeBranches.length)]
    if (parentBranch.branchOrder >= (this.params.maxBranchOrder || 4)) return

    const direction = parentBranch.growthDirection.clone()
    direction.add(this.randomVec3(0.5)).normalize()

    const newBranch = this.createDendriticBranch(
      neuron,
      parentBranch,
      direction,
      parentBranch.branchOrder + 1,
      this.params.branchLengthRange.min +
        Math.random() *
          (this.params.branchLengthRange.max -
            this.params.branchLengthRange.min) *
          0.5,
    )

    neuron.dendrites.branches.push(newBranch)

    debugLog(`Grew new dendritic branch for neuron ${neuron.id}`)
  }

  // Create axons that extend from neurons and connect to other neurons
  private createAxonsAndConnections(): void {
    debugLog("Creating axons and neural connections...")

    for (let i = 0; i < this.neurons.length; i++) {
      const sourceNeuron = this.neurons[i]
      const connections: Connection[] = []

      // Find target neurons within connection radius
      const nearbyNeurons = this.findNearbyNeurons(sourceNeuron, i)

      // Create axon main branch and terminals for each connection
      sourceNeuron.axon.terminals = []
      const axonPoints: Vector3[] = [sourceNeuron.position.clone()]

      // Create connections with realistic probability
      for (let target of nearbyNeurons) {
        if (connections.length >= (this.params.maxConnectionsPerNeuron || 8))
          break

        const targetNeuron = this.neurons[target.index]

        // Connection probability decreases with distance
        const connectionProb = Math.max(
          0.3,
          1.0 - target.distance / this.params.connectionRadius,
        )

        if (Math.random() < connectionProb) {
          // Create axon terminal that connects to target neuron
          const terminal = this.createAxonTerminal(sourceNeuron, targetNeuron)
          sourceNeuron.axon.terminals.push(terminal)

          // Add axon points along the path to target
          const axonPath = this.createAxonPath(
            sourceNeuron.position,
            targetNeuron.position,
          )
          axonPoints.push(...axonPath)

          const connection: Connection = {
            targetNeuronId: targetNeuron.id,
            weight:
              this.params.synapticStrengthRange.min +
              Math.random() *
                (this.params.synapticStrengthRange.max -
                  this.params.synapticStrengthRange.min),
            delay: Math.max(
              5,
              (this.params.synapticDelay || 8) +
                target.distance / (this.params.conductionSpeed || 60),
            ),
            lastUsed: 0,
            usageCount: 0,
            synapseType: sourceNeuron.neuronType,
            plasticity: 0.1 + Math.random() * 0.2,
          }

          connections.push(connection)

          // Create synapses on target neuron's dendrites
          this.createSynapse(sourceNeuron, targetNeuron, connection)
        }
      }

      // Store axon main branch points
      sourceNeuron.axon.mainBranch = axonPoints
      sourceNeuron.connections = connections
    }

    const totalConnections = this.neurons.reduce(
      (sum, n) => sum + n.connections.length,
      0,
    )
    debugLog(`Created ${totalConnections} axonal connections`)
    debugLog(
      `Average connections per neuron: ${(totalConnections / this.neurons.length).toFixed(1)}`,
    )
  }

  private createAxonTerminal(
    sourceNeuron: Neuron,
    targetNeuron: Neuron,
  ): AxonTerminal {
    // Position terminal near target neuron's dendritic field
    const direction = targetNeuron.position
      .clone()
      .sub(sourceNeuron.position)
      .normalize()
    const distance = sourceNeuron.position.distanceTo(targetNeuron.position)
    const terminalPosition = sourceNeuron.position
      .clone()
      .add(direction.multiplyScalar(distance * 0.9))

    return {
      position: terminalPosition,
      targetNeuronId: targetNeuron.id,
      synapses: [],
    }
  }

  private createAxonPath(from: Vector3, to: Vector3): Vector3[] {
    const path: Vector3[] = []
    const direction = to.clone().sub(from)
    const distance = direction.length()
    const segments = Math.max(3, Math.floor(distance / 10)) // One segment per 10 units

    // Create curved axon path (axons don't travel in straight lines)
    for (let i = 1; i < segments; i++) {
      const t = i / segments
      const point = from.clone().lerp(to, t)

      // Add slight curvature and randomness
      const perpendicular = new this.THREE.Vector3(
        (Math.random() - 0.5) * 5,
        (Math.random() - 0.5) * 5,
        (Math.random() - 0.5) * 5,
      )

      // More curvature in the middle of the axon
      const curveFactor = Math.sin(t * Math.PI) * 0.3
      point.add(perpendicular.multiplyScalar(curveFactor))

      path.push(point)
    }

    return path
  }

  private createSimpleDendrites(neuron: Neuron): void {
    const dendrites = neuron.dendrites
    dendrites.branches = []

    // REMOVED: No dendrites directly from cell body - these were the stubby spikes
    // Dendrites will only be created from axon terminals of other neurons
    // This eliminates the anatomically incorrect spikes coming from neuron bodies

    dendrites.totalLength = 0
    dendrites.complexity = 0
  }

  private createSimpleDendrite(
    neuron: Neuron,
    direction: Vector3,
    length: number,
    branchOrder: number,
  ): DendriticBranch {
    const startPos = neuron.position.clone()
    const endPos = startPos
      .clone()
      .add(direction.clone().multiplyScalar(length))

    const points = [startPos, endPos]
    const curve = new this.THREE.CatmullRomCurve3(points)

    return {
      id: this.generateId("branch"),
      parentBranch: null,
      children: [],
      points,
      curve,
      diameter: 0.3 - branchOrder * 0.1,
      length: curve.getLength(),
      activity: 0,
      strength: 0.5 + Math.random() * 0.5,
      lastActive: 0,
      branchOrder,
      synapses: [],
      growthDirection: direction.clone(),
      isGrowing: false,
    }
  }

  private createSimpleConnections(): void {
    debugLog("Creating stable axon connections with dendrites...")

    if (!this.octree) {
      debugLog("Octree not initialized, falling back to brute-force connections.");
      // fallback to old method if octree is missing
      return;
    }
    for (let i = 0; i < this.neurons.length; i++) {
      const sourceNeuron = this.neurons[i];
      const connections: Connection[] = [];
      sourceNeuron.axon.terminals = [];

      // --- Use Octree for fast nearest-neighbor queries ---
      const nearbyNeurons = this.octree.query(
        sourceNeuron.position,
        this.params.connectionRadius,
      ).filter(n => n.id !== sourceNeuron.id);

      // Sort by distance and take closest ones for STABLE connections
      nearbyNeurons.sort((a, b) =>
        sourceNeuron.position.distanceTo(a.position) -
        sourceNeuron.position.distanceTo(b.position)
      );
      const maxConnections = Math.min(
        this.params.maxConnectionsPerNeuron,
        nearbyNeurons.length,
      );

      for (let k = 0; k < maxConnections; k++) {
        const targetNeuron = nearbyNeurons[k];
        const distance = sourceNeuron.position.distanceTo(targetNeuron.position);

        // Create STABLE axon connection
        const connection: Connection = {
          targetNeuronId: targetNeuron.id,
          weight:
            this.params.synapticStrengthRange.min +
            Math.random() *
              (this.params.synapticStrengthRange.max -
                this.params.synapticStrengthRange.min),
          delay:
            (this.params.synapticDelay || 8) +
            distance / (this.params.conductionSpeed || 60),
          lastUsed: 0,
          usageCount: 0,
          synapseType: sourceNeuron.neuronType,
          plasticity: 0.05, // Lower plasticity for stability
        };

        connections.push(connection);

        // Create axon terminal positioned between source and target
        const direction = targetNeuron.position
          .clone()
          .sub(sourceNeuron.position)
          .normalize();
        const terminalPosition = targetNeuron.position
          .clone()
          .sub(direction.clone().multiplyScalar(5));

        const terminal: AxonTerminal = {
          position: terminalPosition,
          targetNeuronId: targetNeuron.id,
          synapses: [],
        };

        sourceNeuron.axon.terminals.push(terminal);

        // Create dendrite from this axon terminal (anatomically correct)
        this.createDendriteFromAxonTerminal(terminal, targetNeuron);
      }

      sourceNeuron.connections = connections;
    }

    const totalConnections = this.neurons.reduce(
      (sum, n) => sum + n.connections.length,
      0,
    )
    debugLog(`Created ${totalConnections} stable connections with dendrites`)
  }

  private createDendriteFromAxonTerminal(
    terminal: AxonTerminal,
    targetNeuron: Neuron,
  ): void {
    if (!this.params.enableDendrites) return // Do not create dendrites if disabled

    // Create a dendrite that extends from the axon terminal to the target neuron
    const startPos = terminal.position.clone()
    const endPos = targetNeuron.position.clone()

    // Add some branching along the way
    const points = [startPos]

    const direction = endPos.clone().sub(startPos).normalize()
    const totalDistance = startPos.distanceTo(endPos)
    const segments = Math.max(2, Math.floor(totalDistance / 3))

    for (let i = 1; i < segments; i++) {
      const t = i / segments
      const point = startPos.clone().lerp(endPos, t)

      // Add slight variation to make it more organic
      const variation = new this.THREE.Vector3(
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
        (Math.random() - 0.5) * 2,
      )
      point.add(variation)
      points.push(point)
    }

    points.push(endPos)

    const dendrite: DendriticBranch = {
      id: this.generateId("dendrite"),
      parentBranch: null,
      children: [],
      points: points,
      curve: new this.THREE.CatmullRomCurve3(points),
      diameter: this.params.dendriteRadius || 0.15,
      length: totalDistance,
      activity: 0,
      strength: 0.8,
      lastActive: 0,
      branchOrder: 0,
      synapses: [],
      growthDirection: direction,
      isGrowing: false,
    }

    targetNeuron.dendrites.branches.push(dendrite)
    targetNeuron.dendrites.totalLength += dendrite.length
    targetNeuron.dendrites.complexity += 1
  }

  // Get all dendritic branches for rendering
  public getAllDendriticBranches(): DendriticBranch[] {
    const allBranches: DendriticBranch[] = []
    for (let neuron of this.neurons) {
      allBranches.push(...neuron.dendrites.branches)
    }
    return allBranches
  }

  public pruneUnattachedNeurons(): void {
    const connectedNeuronIds = new Set<string>()

    // Collect all neurons that are targets of a connection
    for (const neuron of this.neurons) {
      for (const connection of neuron.connections) {
        connectedNeuronIds.add(connection.targetNeuronId)
      }
      // Also consider neurons with outgoing connections as "attached"
      if (neuron.connections.length > 0) {
        connectedNeuronIds.add(neuron.id)
      }
    }

    const initialCount = this.neurons.length
    this.neurons = this.neurons.filter((neuron) =>
      connectedNeuronIds.has(neuron.id),
    )
    const finalCount = this.neurons.length
    debugLog(`Pruned ${initialCount - finalCount} unattached neurons.`)
  }

  // Network dynamics - growing and dying neurons
  public growNewNeuron(nearPosition: Vector3): Neuron | null {
    if (this.neurons.length >= 150) return null // Limit total neurons

    // Create new neuron near an active area
    const position = nearPosition
      .clone()
      .add(
        new this.THREE.Vector3(
          (Math.random() - 0.5) * 20,
          (Math.random() - 0.5) * 20,
          (Math.random() - 0.5) * 20,
        ),
      )

    const neuron: Neuron = {
      id: this.generateId("neuron"),
      position: position,
      activation: 0,
      excitability: 0.7 + Math.random() * 0.3,
      refractoryPeriod: this.params.refractoryPeriod || 80,
      lastFired: 0,
      glowIntensity: 1,
      lastActivation: 0,
      connections: [],
      dendrites: { branches: [], totalLength: 0, complexity: 0 },
      axon: {
        mainBranch: [],
        terminals: [],
        myelinated: true,
        conductionSpeed: this.params.conductionSpeed || 60,
      },
      neuronType: Math.random() > 0.15 ? "excitatory" : "inhibitory",
      health: 1.0,
      age: 0,
      layer: 0,
    }

    this.neurons.push(neuron)

    // Create connections to nearby neurons
    this.createConnectionsForNewNeuron(neuron)

    debugLog(`Grew new neuron ${neuron.id} at position`, position)
    return neuron
  }

  public removeInactiveNeuron(neuronId: string): boolean {
    const neuronIndex = this.neurons.findIndex((n) => n.id === neuronId);
    if (neuronIndex === -1) return false;

    const neuron = this.neurons[neuronIndex]

    // Remove all connections to this neuron from other neurons
    for (let otherNeuron of this.neurons) {
      otherNeuron.connections = otherNeuron.connections.filter(
        (conn) => conn.targetNeuronId !== neuronId,
      )

      // Remove axon terminals pointing to this neuron
      otherNeuron.axon.terminals = otherNeuron.axon.terminals.filter(
        (terminal) => terminal.targetNeuronId !== neuronId,
      )
    }

    // Remove the neuron
    this.neurons.splice(neuronIndex, 1)

    debugLog(`Removed inactive neuron ${neuronId}`)
    return true
  }

  private createConnectionsForNewNeuron(newNeuron: Neuron): void {
    // Connect to nearby neurons
    const nearbyNeurons = this.neurons
      .filter(
        (n) =>
          n.id !== newNeuron.id &&
          n.position.distanceTo(newNeuron.position) <=
            this.params.connectionRadius,
      )
      .slice(0, this.params.maxConnectionsPerNeuron)

    for (let targetNeuron of nearbyNeurons) {
      const connection: Connection = {
        targetNeuronId: targetNeuron.id,
        weight:
          this.params.synapticStrengthRange.min +
          Math.random() *
            (this.params.synapticStrengthRange.max -
              this.params.synapticStrengthRange.min),
        delay: this.params.synapticDelay || 8,
        lastUsed: 0,
        usageCount: 0,
        synapseType: newNeuron.neuronType,
        plasticity: 0.05,
      }

      newNeuron.connections.push(connection)

      // Create axon terminal
      const terminal: AxonTerminal = {
        position: targetNeuron.position.clone(),
        targetNeuronId: targetNeuron.id,
        synapses: [],
      }

      newNeuron.axon.terminals.push(terminal)
    }
  }
}

// --- Octree spatial index for fast nearest-neighbor queries ---
class Octree {
  bounds: { min: Vector3; max: Vector3 };
  maxDepth: number;
  maxItems: number;
  items: Array<{ item: Neuron; pos: Vector3 }>;
  children: Octree[] | null;
  depth: number;
  constructor(bounds: { min: Vector3; max: Vector3 }, maxDepth = 6, maxItems = 8) {
    this.bounds = bounds;
    this.maxDepth = maxDepth;
    this.maxItems = maxItems;
    this.items = [];
    this.children = null;
    this.depth = 0;
  }
  insert(item: Neuron, pos: Vector3) {
    if (this.children) {
      this._childFor(pos).insert(item, pos);
      return;
    }
    this.items.push({ item, pos });
    if (this.items.length > this.maxItems && this.depth < this.maxDepth) {
      this._subdivide();
      for (const { item, pos } of this.items) {
        this._childFor(pos).insert(item, pos);
      }
      this.items = [];
    }
  }
  query(pos: Vector3, radius: number, found: Neuron[] = []): Neuron[] {
    if (!this._intersectsSphere(pos, radius)) return found;
    if (this.children) {
      for (const child of this.children) child.query(pos, radius, found);
    } else {
      for (const { item, pos: ipos } of this.items) {
        const dx = pos.x - ipos.x, dy = pos.y - ipos.y, dz = pos.z - ipos.z;
        if (dx * dx + dy * dy + dz * dz <= radius * radius) found.push(item);
      }
    }
    return found;
  }
  _subdivide() {
    this.children = [];
    const { min, max } = this.bounds;
    const mid = min.clone().add(max).multiplyScalar(0.5);
    for (let xi = 0; xi < 2; xi++)
      for (let yi = 0; yi < 2; yi++)
        for (let zi = 0; zi < 2; zi++) {
          const childMin = min.clone();
          childMin.x = xi ? mid.x : min.x;
          childMin.y = yi ? mid.y : min.y;
          childMin.z = zi ? mid.z : min.z;
          const childMax = max.clone();
          childMax.x = xi ? max.x : mid.x;
          childMax.y = yi ? max.y : mid.y;
          childMax.z = zi ? max.z : mid.z;
          this.children.push(
            new Octree(
              { min: childMin, max: childMax },
              this.maxDepth,
              this.maxItems,
            ),
          );
          this.children[this.children.length - 1].depth = this.depth + 1;
        }
  }
  _childFor(pos: Vector3): Octree {
    const { min, max } = this.bounds;
    const mid = min.clone().add(max).multiplyScalar(0.5);
    let idx = 0;
    if (pos.x > mid.x) idx += 4;
    if (pos.y > mid.y) idx += 2;
    if (pos.z > mid.z) idx += 1;
    return this.children![idx];
  }
  _intersectsSphere(pos: Vector3, radius: number): boolean {
    const { min, max } = this.bounds;
    let d = 0;
    // Direct property access for x, y, z
    if (pos.x < min.x) d += (min.x - pos.x) ** 2;
    else if (pos.x > max.x) d += (pos.x - max.x) ** 2;
    if (pos.y < min.y) d += (min.y - pos.y) ** 2;
    else if (pos.y > max.y) d += (pos.y - max.y) ** 2;
    if (pos.z < min.z) d += (min.z - pos.z) ** 2;
    else if (pos.z > max.z) d += (pos.z - max.z) ** 2;
    return d <= radius * radius;
  }
}
